import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SparklesIcon, BoltIcon } from '@heroicons/react/24/outline';

const Hero = () => {
  const navigate = useNavigate();

  const handleBrowseCompetitions = () => navigate('/competitions');
  const handleJoinNow = () => navigate('/register');

  return (
    <div className="relative overflow-hidden bg-[#050505] pt-32 pb-20 md:pt-48 md:pb-32">
      {/* --- Visual Backdrop --- */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-50 mix-blend-overlay pointer-events-none" />
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:40px_40px] [mask-image:radial-gradient(white,transparent_85%)]" />
      
      {/* Subtle Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-500/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 text-center">
        
        {/* 1. THE GENZ BADGE (Updated) */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/5 mb-8 shadow-[0_0_15px_rgba(59,130,246,0.1)]"
        >
          <div className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
          <span className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-400/90">
            GenZ's are here <span className="text-gray-700 mx-1">•</span> The Takeover
          </span>
        </motion.div>
        
        {/* 2. Heading */}
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-5xl md:text-7xl font-black tracking-tighter mb-6 leading-none"
        >
          <span className="text-white italic">CREATE.</span>
          <span className="text-gray-600 italic"> SKILL.</span>
          <br className="md:hidden" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 italic"> ELEVATE.</span>
        </motion.h1>
        
        {/* 3. Subtext */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-sm md:text-base text-gray-500 max-w-lg mx-auto mb-10 leading-relaxed font-medium italic"
        >
          Turning raw talent into digital dominance. This isn't just a platform; it's the 
          <span className="text-gray-300 ml-1">cultural benchmark</span> for the next generation.
        </motion.p>
        
        {/* 4. Metric Grid */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-row justify-center items-center gap-6 md:gap-12 mb-12 border-y border-white/5 py-6"
        >
          <div className="text-center">
            <div className="text-xl md:text-2xl font-black text-white italic tracking-tighter uppercase">Active</div>
            <div className="text-[8px] text-gray-600 font-bold uppercase tracking-widest">Arena Status</div>
          </div>
          <div className="h-8 w-px bg-white/5" />
          <div className="text-center">
            <div className="text-xl md:text-2xl font-black text-white italic tracking-tighter uppercase">Global</div>
            <div className="text-[8px] text-gray-600 font-bold uppercase tracking-widest">Reach Tier</div>
          </div>
          <div className="h-8 w-px bg-white/5" />
          <div className="text-center">
            <div className="text-xl md:text-2xl font-black text-white italic tracking-tighter uppercase">Elite</div>
            <div className="text-[8px] text-gray-600 font-bold uppercase tracking-widest">Network Level</div>
          </div>
        </motion.div>
        
        {/* 5. CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <button 
            onClick={handleBrowseCompetitions}
            className="w-full sm:w-auto px-10 py-5 bg-white text-black font-black rounded-2xl text-[10px] uppercase tracking-[0.2em] hover:bg-blue-600 hover:text-white transition-all duration-500 shadow-xl flex items-center justify-center gap-2 group"
          >
            Enter Arena <BoltIcon className="h-4 w-4 group-hover:fill-current" />
          </button>
          
          <button 
            onClick={handleJoinNow}
            className="w-full sm:w-auto px-10 py-5 bg-transparent text-white font-black rounded-2xl text-[10px] uppercase tracking-[0.2em] border border-white/10 hover:bg-white/5 transition-all duration-500 flex items-center justify-center gap-2"
          >
            Claim Identity <SparklesIcon className="h-4 w-4" />
          </button>
        </motion.div>

      </div>
    </div>
  );
};

export default Hero;